import * as THREE from 'three';
import useEditorStore from '../../shared/stores/useEditorStore';
import { serializeMaterial, deserializeMaterial } from '../../shared/lib/serialization';
import { styleCompareOverlayMaterial } from '../../shared/constants/materialConfig';
import { syncCompareBaseline } from '../../shared/lib/compareBaselineSnapshot';
import { stripPreviewTint } from '../../shared/lib/previewTint';

/**
 * In-memory mesh payloads (BufferGeometry clones). Kept outside Zustand to avoid
 * serializing heavy GPU buffers into devtools / persist middleware.
 * @type {Map<string, import('./types').MeshSnapshotPayload>}
 */
const meshPool = new Map();

function cloneUserData(userData) {
  if (!userData || typeof userData !== 'object') return {};
  try {
    return JSON.parse(JSON.stringify(userData));
  } catch {
    return { ...userData };
  }
}

function disposePayload(payload) {
  if (!payload) return;
  payload.shadedGeometry?.dispose?.();
  payload.unmodifiedGeometry?.dispose?.();
}

/**
 * @param {string} stepId
 * @returns {boolean}
 */
export function captureMeshForStep(stepId) {
  const { sceneRefs } = useEditorStore.getState();
  const shadedMesh = sceneRefs?.shadedMesh;
  if (!shadedMesh?.geometry?.attributes?.position) return false;

  const existing = meshPool.get(stepId);
  if (existing) disposePayload(existing);

  const unmodifiedMesh = sceneRefs?.unmodifiedMesh;
  const material = sceneRefs?.material ?? shadedMesh.material;

  // The autosave fires on a debounce while a tool is still open, so the socket can
  // be carrying a preview tint (measure reduction paints its band zones onto the
  // real vertex colours). Restoring this entry — a revert, a reload — must bring
  // back the socket, not the overlay that happened to be on it.
  const shadedGeometry = shadedMesh.geometry.clone();
  stripPreviewTint(shadedGeometry);

  meshPool.set(stepId, {
    shadedGeometry,
    unmodifiedGeometry: unmodifiedMesh?.geometry ? unmodifiedMesh.geometry.clone() : null,
    // Whether that geometry is a real original or a stand-in cloned from an
    // already-modified socket — the measuring tools treat the two differently.
    unmodifiedSource: unmodifiedMesh?.userData?.compareBaselineSource ?? null,
    position: shadedMesh.position.toArray(),
    quaternion: shadedMesh.quaternion.toArray(),
    scale: shadedMesh.scale.toArray(),
    meshName: shadedMesh.name || 'ShadedMesh',
    userData: cloneUserData(shadedMesh.userData),
    materialData: material ? serializeMaterial(material) : null,
    showUnmodifiedMesh: useEditorStore.getState().showUnmodifiedMesh,
  });

  return true;
}

export function hasMeshForStep(stepId) {
  return meshPool.has(stepId);
}

/**
 * @param {string} stepId
 * @returns {boolean}
 */
export function restoreMeshForStep(stepId) {
  const payload = meshPool.get(stepId);
  if (!payload) return false;

  const editor = useEditorStore.getState();
  const { sceneRefs, replaceShadedMesh, setSceneRefs } = editor;
  const scene = sceneRefs?.scene;
  if (!scene) return false;

  const hasVertexColors = !!payload.shadedGeometry.attributes.color;
  let material = null;

  if (payload.materialData) {
    material = deserializeMaterial(payload.materialData);
  }
  if (!material) {
    material = new THREE.MeshLambertMaterial({
      color: hasVertexColors ? 0xffffff : 0xe8d5b7,
      side: THREE.DoubleSide,
      vertexColors: hasVertexColors,
    });
  } else if (hasVertexColors) {
    material.vertexColors = true;
  }
  material.needsUpdate = true;

  const mesh = new THREE.Mesh(payload.shadedGeometry.clone(), material);
  mesh.name = payload.meshName;
  mesh.position.fromArray(payload.position);
  mesh.quaternion.fromArray(payload.quaternion);
  mesh.scale.fromArray(payload.scale);
  mesh.matrixAutoUpdate = true;
  mesh.updateMatrixWorld(true);
  mesh.userData = cloneUserData(payload.userData);

  const oldShaded = sceneRefs.shadedMesh;
  const oldUnmodified = sceneRefs.unmodifiedMesh;

  if (oldShaded) {
    scene.remove(oldShaded);
    oldShaded.geometry?.dispose?.();
    if (oldShaded.material && oldShaded.material !== material) {
      oldShaded.material?.dispose?.();
    }
  }
  if (oldUnmodified) {
    scene.remove(oldUnmodified);
    if (oldUnmodified.geometry !== oldShaded?.geometry) {
      oldUnmodified.geometry?.dispose?.();
    }
    oldUnmodified.material?.dispose?.();
  }

  scene.add(mesh);

  let unmodifiedMesh = null;
  if (payload.unmodifiedGeometry) {
    const overlayMaterial = new THREE.MeshMatcapMaterial({
      vertexColors: false,
      color: '#a8d8f0',
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -0.1,
    });
    unmodifiedMesh = new THREE.Mesh(payload.unmodifiedGeometry.clone(), overlayMaterial);
    unmodifiedMesh.userData.compareBaselineSource = payload.unmodifiedSource ?? 'currentMesh';
    unmodifiedMesh.position.copy(mesh.position);
    unmodifiedMesh.quaternion.copy(mesh.quaternion);
    unmodifiedMesh.scale.copy(mesh.scale);
    unmodifiedMesh.updateMatrixWorld(true);
    unmodifiedMesh.visible = payload.showUnmodifiedMesh;
    unmodifiedMesh.renderOrder = 5e5;
    styleCompareOverlayMaterial(unmodifiedMesh.material, unmodifiedMesh.visible);
    scene.add(unmodifiedMesh);
    // Step payloads carry the baseline in that step's frame (pre/post alignment
    // bake). Re-point the load-time snapshot at it so a checkpoint reload from
    // here restores an overlay registered against the socket on screen.
    syncCompareBaseline(unmodifiedMesh);
  }

  const normalMaterial = sceneRefs.normalMaterial ?? new THREE.MeshNormalMaterial({ side: THREE.DoubleSide });

  // Without a baseline in the payload there is no original to compare against — don't leave the
  // Compare button lit over an empty overlay.
  useEditorStore.setState({ showUnmodifiedMesh: !!unmodifiedMesh && payload.showUnmodifiedMesh });
  setSceneRefs({
    ...sceneRefs,
    shadedMesh: mesh,
    material: mesh.material,
    unmodifiedMesh,
    normalMaterial,
  });

  editor.incrementGeometryVersion();
  return true;
}

export function clearMeshPool() {
  for (const payload of meshPool.values()) {
    disposePayload(payload);
  }
  meshPool.clear();
}

export function clearMeshForStep(stepId) {
  const payload = meshPool.get(stepId);
  if (payload) {
    disposePayload(payload);
    meshPool.delete(stepId);
  }
}

/** Schema tag for cached mesh payloads; bump when the shape below changes. */
export const MESH_CACHE_SCHEMA_VERSION = 1;

/** BufferGeometry → structured-clone-safe plain object (typed arrays only). */
export function serializeGeometry(geometry) {
  if (!geometry?.attributes) return null;
  const attributes = {};
  for (const name of Object.keys(geometry.attributes)) {
    const attr = geometry.attributes[name];
    if (!attr?.array) continue;
    attributes[name] = {
      array: attr.array,
      itemSize: attr.itemSize,
      normalized: !!attr.normalized,
    };
  }
  const index = geometry.index?.array ? { array: geometry.index.array } : null;
  return { attributes, index };
}

/** Plain object (from {@link serializeGeometry}) → THREE.BufferGeometry. */
export function deserializeGeometry(data) {
  if (!data?.attributes) return null;
  const geometry = new THREE.BufferGeometry();
  for (const [name, attr] of Object.entries(data.attributes)) {
    if (!attr?.array) continue;
    geometry.setAttribute(
      name,
      new THREE.BufferAttribute(attr.array, attr.itemSize, attr.normalized),
    );
  }
  if (data.index?.array) {
    geometry.setIndex(new THREE.BufferAttribute(data.index.array, 1));
  }
  if (!geometry.attributes.normal) geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Snapshot a captured step's mesh pool entry as a structured-clone-safe object
 * for IndexedDB persistence. Returns null when the step has no captured mesh.
 * @param {string} stepId
 */
export function serializeStepMesh(stepId) {
  const payload = meshPool.get(stepId);
  if (!payload?.shadedGeometry) return null;
  return {
    schemaVersion: MESH_CACHE_SCHEMA_VERSION,
    shadedGeometry: serializeGeometry(payload.shadedGeometry),
    unmodifiedGeometry: payload.unmodifiedGeometry
      ? serializeGeometry(payload.unmodifiedGeometry)
      : null,
    position: payload.position,
    quaternion: payload.quaternion,
    scale: payload.scale,
    meshName: payload.meshName,
    userData: payload.userData,
    materialData: payload.materialData,
    showUnmodifiedMesh: payload.showUnmodifiedMesh,
  };
}

/**
 * Rebuild the in-memory mesh pool entry for a step from a persisted snapshot,
 * so {@link restoreMeshForStep} can install it exactly as an in-session capture.
 * @param {string} stepId
 * @param {ReturnType<typeof serializeStepMesh>} serialized
 * @returns {boolean}
 */
export function hydrateStepMesh(stepId, serialized) {
  const shadedGeometry = deserializeGeometry(serialized?.shadedGeometry);
  if (!shadedGeometry) return false;

  const existing = meshPool.get(stepId);
  if (existing) disposePayload(existing);

  meshPool.set(stepId, {
    shadedGeometry,
    unmodifiedGeometry: serialized.unmodifiedGeometry
      ? deserializeGeometry(serialized.unmodifiedGeometry)
      : null,
    position: serialized.position ?? [0, 0, 0],
    quaternion: serialized.quaternion ?? [0, 0, 0, 1],
    scale: serialized.scale ?? [1, 1, 1],
    meshName: serialized.meshName ?? 'ShadedMesh',
    userData: cloneUserData(serialized.userData),
    materialData: serialized.materialData ?? null,
    showUnmodifiedMesh: !!serialized.showUnmodifiedMesh,
  });
  return true;
}
